import { CountUp } from 'countup.js';

let rangeSliders = [];
let apprPercEl = null;
let divElementEl = null;
let exitTextHolderEl = null;

const unlockShare = new CountUp('unlockShare', 0);
const homeownerShare = new CountUp('homeownerShare', 0);
const homeValue = new CountUp('homeValue', 0);
const acl = new CountUp('acl', 0, { decimalPlaces: 1 });

let chartCollapsed = false;

// Only init if calculator exists on page
if (document.querySelector('.calculator')) {
  document.addEventListener('DOMContentLoaded', calculatorInit);
}

function calculatorInit() {
  // Cache elements after DOM is ready
  rangeSliders = Array.from(document.querySelectorAll('.appreciation-slider'));
  apprPercEl = document.querySelector('.percent-selected');
  divElementEl = document.querySelector('.appreciation-type');
  exitTextHolderEl = document.querySelector('.exit-text-holder');

  calculator();
}

function calculator() {
  rangeValue();
  updateChart();

  // Slider events
  for (const slider of rangeSliders) {
    slider.addEventListener('input', () => {
      rangeValue();
      updateChart();
    });
    slider.addEventListener('change', () => {
      rangeValue();
      updateChart();
    });
    slider.addEventListener('touchmove', handleTouchMove, { passive: false });
  }

  // Current home input
  const currentHomeInput = document.getElementById('current-home');
  if (currentHomeInput) {
    currentHomeInput.addEventListener('keyup', () => {
      updateChart();
    });
  }

  // HEI value input
  const heiInput = document.getElementById('hei-value');
  if (heiInput) {
    heiInput.addEventListener('keyup', () => {
      const valStr = heiInput.value.replaceAll(/[$,]/g, '');
      const val = Number.parseInt(valStr, 10) || 0;
      const heaRequest = document.getElementById('hea-request');

      if (val < 15_000) {
        if (heaRequest) heaRequest.classList.add('d-none');
        heiInput.classList.add('is-invalid');
      } else {
        if (heaRequest) heaRequest.classList.remove('d-none');
        heiInput.classList.remove('is-invalid');
      }

      updateChart();
    });
  }

  // Chart column click handlers
  const chartCols = Array.from(document.querySelectorAll('.chart-col'));
  for (const col of chartCols) {
    col.addEventListener('click', () => {
      for (const c of chartCols) c.classList.remove('active');
      col.classList.add('active');

      const unlockVal = Number.parseInt(col.dataset.unlockVal || '0', 10) || 0;
      const homeownerVal = Number.parseInt(col.dataset.homeownerVal || '0', 10) || 0;
      const aclNum = Number.parseFloat(col.dataset.acl || '0') || 0;

      unlockShare.update(unlockVal);
      homeownerShare.update(homeownerVal);
      homeValue.update(unlockVal + homeownerVal);

      const aclVal = (Math.round(aclNum * 1000) / 10).toFixed(1).replace(/\.0$/, '');
      acl.update(aclVal);

      const year = col.dataset.year || '';
      const appreciation = getApprPercText();
      updateExitText(year, appreciation);
    });
  }
}

function rangeValue() {
  if (!rangeSliders.length || !apprPercEl || !divElementEl) return;

  const slider = rangeSliders[0];
  const val = Number.parseInt(slider.value, 10);

  switch (val) {
    case 0: {
      setApprText('-2', 'Depreciation: -2%');
      break;
    }

    case 1: {
      setApprText('-1', 'Depreciation: -1%');
      break;
    }

    case 2: {
      setApprText('0', 'No Change');
      break;
    }

    case 3: {
      setApprText('1', 'Low growth: 1%');
      break;
    }

    case 4: {
      setApprText('2', 'Average growth: 2%');
      break;
    }

    case 5: {
      setApprText('3', 'High growth: 3%');
      break;
    }

    case 6: {
      setApprText('4', 'High growth: 4%');
      break;
    }

    case 7: {
      setApprText('5', 'High growth: 5%');
      break;
    }

    case 8: {
      setApprText('6', 'High growth: 6%');
      break;
    }

    case 9: {
      setApprText('7', 'High growth: 7%');
      break;
    }

    default: {
      divElementEl.textContent = 'unknown value';
    }
  }
}

function setApprText(percent, label) {
  if (apprPercEl) apprPercEl.textContent = percent;
  if (divElementEl) divElementEl.textContent = label;
}

function getApprPercText() {
  return apprPercEl ? (apprPercEl.textContent || '').trim() : '';
}

// eslint-disable-next-line complexity
function updateChart() {
  const currentHomeEl = document.getElementById('current-home');
  const homeValDesc = document.getElementById('home-value-desc');
  const heiEl = document.getElementById('hei-value');
  const heiValDesc = document.getElementById('hei-value-desc');
  const chartEl = document.getElementById('calculator-chart');

  if (!currentHomeEl || !heiEl || !chartEl) return;

  const homeValStr = currentHomeEl.value.replaceAll(/[$,]/g, '');
  const homeVal = Math.ceil(Number.parseFloat(homeValStr || '0'));

  const heiValStr = heiEl.value.replaceAll(/[^0-9]/g, '');
  const heiVal = Number.parseInt(heiValStr || '0', 10);

  if (!homeVal || !heiVal) return;

  const origPct = heiVal / homeVal; // numeric ratio
  const exchRate = 1.8;
  const estAppreciationStr = getApprPercText();
  const estAppreciation = Number.parseFloat(estAppreciationStr || '0') || 0;

  const maxVal =
    estAppreciation === -1 || estAppreciation === -2
      ? Math.round(homeVal * (1 + estAppreciation / 100) ** 1)
      : Math.round(homeVal * (1 + estAppreciation / 100) ** 10);

  const tooSmallHei = heiVal < 15_000;
  const tooHighRatio = origPct * exchRate > 0.499;

  if (tooSmallHei) {
    heiEl.classList.add('is-invalid');
    if (heiValDesc) heiValDesc.classList.add('d-none');
  } else {
    heiEl.classList.remove('is-invalid');
    if (heiValDesc) heiValDesc.classList.remove('d-none');
  }

  if (tooHighRatio) {
    if (homeValDesc) homeValDesc.classList.add('d-none');
    currentHomeEl.classList.add('is-invalid');
  } else {
    if (homeValDesc) homeValDesc.classList.remove('d-none');
    currentHomeEl.classList.remove('is-invalid');
  }

  if (tooSmallHei || tooHighRatio) {
    collapseChart(chartEl);
    return;
  }

  expandChart(chartEl);

  const years = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  for (const year of years) {
    const rateShare = origPct * exchRate;
    const epc = Math.round(heiVal * (1 + 0.199) ** year);
    const futureVal = Math.round(homeVal * (1 + estAppreciation / 100) ** year);
    const unlockVal = Math.round(futureVal * rateShare);
    const homeownerVal = futureVal - unlockVal;

    const colEls = Array.from(document.querySelectorAll(`[data-year="${year}"]`));
    if (!colEls.length || !maxVal) continue;

    for (const colEl of colEls) {
      colEl.style.height = `${(futureVal / maxVal) * 100 - 10}%`;

      let finalUnlockVal;
      let finalHomeownerVal;
      let finalAcl;

      if (unlockVal > epc) {
        finalUnlockVal = epc;
        finalHomeownerVal = futureVal - epc;
        finalAcl = 0.199;
      } else {
        finalUnlockVal = unlockVal;
        finalHomeownerVal = homeownerVal;
        finalAcl = annualizedRate(unlockVal, heiVal, year);
      }

      colEl.dataset.homeownerVal = String(finalHomeownerVal);
      colEl.dataset.unlockVal = String(finalUnlockVal);
      colEl.dataset.acl = String(finalAcl);

      const unlockBar = colEl.querySelector('.unlock');
      if (unlockBar) {
        unlockBar.style.height = `${(finalUnlockVal / futureVal) * 100}%`;
      }
    }
  }

  updateExitText(10, estAppreciationStr);

  const chartCols = Array.from(document.querySelectorAll('.chart-col'));
  for (const c of chartCols) c.classList.remove('active');

  const year10Col = document.querySelector('[data-year="10"]');
  if (year10Col) year10Col.classList.add('active');

  if (exitTextHolderEl) showElement(exitTextHolderEl);

  const activeCol = document.querySelector('.chart-col.active');
  if (activeCol) {
    const unlockVal = Number.parseInt(activeCol.dataset.unlockVal || '0', 10) || 0;
    const homeownerVal = Number.parseInt(activeCol.dataset.homeownerVal || '0', 10) || 0;
    const aclNum = Number.parseFloat(activeCol.dataset.acl || '0') || 0;

    unlockShare.update(unlockVal);
    homeownerShare.update(homeownerVal);
    homeValue.update(unlockVal + homeownerVal);

    const aclVal = (Math.round(aclNum * 1000) / 10).toFixed(1).replace(/\.0$/, '');
    acl.update(aclVal);
  }
}

function updateExitText(year, appreciation) {
  const yearEl = document.querySelector('.exit-text .year');
  const apprEl = document.querySelector('.exit-text .appreciation');

  if (yearEl) yearEl.textContent = year;
  if (apprEl) apprEl.textContent = `${appreciation}%`;
}

function expandChart(el, dur = 400) {
  if (!el) return;

  // If it's already visible and not collapsed, do nothing
  if (!el.classList.contains('d-none') && !chartCollapsed) {
    return;
  }

  chartCollapsed = false;

  // Make sure it's not display:none so we can measure it
  el.classList.remove('d-none');
  el.style.display = el.style.display || ''; // let CSS decide
  el.style.overflow = 'hidden';
  el.style.height = '0px';
  el.style.opacity = '0';

  // Force reflow so the browser registers height:0
  // eslint-disable-next-line no-unused-expressions
  el.offsetHeight;

  const target = el.scrollHeight;

  el.style.transition = `height ${dur}ms ease, opacity ${dur}ms ease`;
  el.style.height = `${target}px`;
  el.style.opacity = '1';

  const onEnd = (e) => {
    // Only clean up when the height transition finishes
    if (e.propertyName !== 'height') return;

    el.style.height = '';
    el.style.overflow = '';
    el.style.opacity = '';
    el.style.transition = '';
    el.removeEventListener('transitionend', onEnd);
  };

  el.addEventListener('transitionend', onEnd);

  for (const el1 of document.querySelectorAll('.exit-text')) {
    showElement(el1);
  }
}

function collapseChart(el, dur = 400) {
  if (!el) return;

  // If already collapsed / hidden, just ensure exit text is hidden
  if (chartCollapsed || el.classList.contains('d-none')) {
    for (const el1 of document.querySelectorAll('.exit-text')) {
      hideElement(el1);
    }

    chartCollapsed = true;
    return;
  }

  chartCollapsed = true;

  for (const el1 of document.querySelectorAll('.exit-text')) {
    hideElement(el1);
  }

  // Get current height so we can animate down from it
  const current = el.scrollHeight || el.offsetHeight || 0;

  el.style.overflow = 'hidden';
  el.style.height = `${current}px`;
  el.style.opacity = '1';

  // Force reflow before changing to 0
  // eslint-disable-next-line no-unused-expressions
  el.offsetHeight;

  el.style.transition = `height ${dur}ms ease, opacity ${dur}ms ease`;
  el.style.height = '0px';
  el.style.opacity = '0';

  const onEnd = (e) => {
    if (e.propertyName !== 'height') return;

    el.classList.add('d-none');
    el.style.display = '';
    el.style.height = '';
    el.style.overflow = '';
    el.style.opacity = '';
    el.style.transition = '';
    el.removeEventListener('transitionend', onEnd);
  };

  el.addEventListener('transitionend', onEnd);
}

function showElement(el) {
  el.classList.remove('d-none');
  el.style.display = '';
}

function hideElement(el) {
  el.classList.add('d-none');
}

function annualizedRate(finalAmt, initialAmt, years) {
  if (initialAmt <= 0 || years <= 0) return 0;
  return (finalAmt / initialAmt) ** (1 / years) - 1;
}

function handleTouchMove(event) {
  event.preventDefault();

  const el = event.target;
  const min = Number(el.min);
  const max = Number(el.max);
  const step = Number(el.step);
  const rect = el.getBoundingClientRect();
  const touch = event.touches[0];
  const relativePos = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
  const newValue = snapToStep(relativePos * (max - min) + min, step);

  if (Number(el.value) !== newValue) {
    el.value = newValue;
    el.dispatchEvent(new Event('input'));
  }
}

function snapToStep(value, step) {
  const inverse = 1 / step;
  return Math.round(value * inverse) / inverse;
}
